import {match, Pattern} from 'ts-pattern';

import type {
  CustomerInsightsRecord,
  CustomerInsightsSection,
  CustomerInsightsViewModel,
} from '../types/CustomerInsightsViewModel';
import {getNewestCustomerInsightsRecords} from './getNewestCustomerInsightsRecords';

export type CustomerInsightsOpenGroupName =
  | 'openInterests'
  | 'openBusinessCases'
  | 'openServiceCases';

export interface CustomerInsightsOpenGroup {
  name: CustomerInsightsOpenGroupName;
  records: CustomerInsightsRecord[];
  /** Open records of this kind in total, not just the ones listed. */
  total: number | undefined;
}

export interface CustomerInsightsContentState {
  failedRetries: VoidFunction[];
  showFailure: boolean;
  showPartialFailure: boolean;
  showEmptyCustomer: boolean;
  showLastContact: boolean;
  showAlerts: boolean;
  hasAlertsLoading: boolean;
  firstLoadingSummarySection: 'lastContact' | 'overdueInvoices' | 'overdueTasks' | undefined;
  openGroups: CustomerInsightsOpenGroup[];
  showOpenItems: boolean;
  hasOpenItemsLoading: boolean;
  openItemsCount: number | undefined;
  showCurrentActivity: boolean;
}

const isVisibleSection = <T>(section: CustomerInsightsSection<T>) =>
  match(section.status)
    .with('loading', 'ready', 'degraded', () => true)
    .otherwise(() => false);

const isReadySection = <T>(section: CustomerInsightsSection<T>) =>
  match(section.status)
    .with('ready', 'degraded', () => true)
    .otherwise(() => false);

const isLoadingSection = (section: Pick<CustomerInsightsSection<never>, 'status'>) =>
  section.status === 'loading';

const getActivitySections = (viewModel: CustomerInsightsViewModel) => [
  viewModel.lastContact,
  viewModel.overdueInvoices,
  viewModel.overdueTasks,
  viewModel.openInterests,
  viewModel.openBusinessCases,
  viewModel.openServiceCases,
  viewModel.pastSales,
  viewModel.pastPurchases,
  viewModel.unsuccessfulInterests,
  viewModel.serviceHistory,
];

const isEntireActivityEmpty = (viewModel: CustomerInsightsViewModel) =>
  getActivitySections(viewModel).every((section) =>
    match(section.status)
      .with('empty', 'omitted', () => true)
      .otherwise(() => false)
  );

const getFailedRetries = (viewModel: CustomerInsightsViewModel) =>
  Array.from(
    new Set(
      [viewModel.identity, ...getActivitySections(viewModel)].flatMap((section) =>
        match(section)
          .with({status: 'error'}, {status: 'degraded'}, ({retry}) => [retry])
          .otherwise(() => [])
      )
    )
  );

const hasUsableOrLoadingActivity = (viewModel: CustomerInsightsViewModel) =>
  getActivitySections(viewModel).some((section) =>
    match(section.status)
      .with('ready', 'degraded', 'loading', () => true)
      .otherwise(() => false)
  );

export function getCustomerInsightsContentState(
  viewModel: CustomerInsightsViewModel
): CustomerInsightsContentState {
  const failedRetries = getFailedRetries(viewModel);
  const showFailure =
    viewModel.identity.status === 'error' ||
    (failedRetries.length > 0 && !hasUsableOrLoadingActivity(viewModel));
  const showPartialFailure =
    !showFailure && failedRetries.length > 0 && hasUsableOrLoadingActivity(viewModel);
  const showEmptyCustomer = isEntireActivityEmpty(viewModel) && !showFailure;
  const showLastContact = isVisibleSection(viewModel.lastContact);
  const alertSections = [viewModel.overdueInvoices, viewModel.overdueTasks] as const;
  const summarySections = [
    {name: 'lastContact' as const, section: viewModel.lastContact},
    {name: 'overdueInvoices' as const, section: viewModel.overdueInvoices},
    {name: 'overdueTasks' as const, section: viewModel.overdueTasks},
  ];
  const showAlerts = alertSections.some(isReadySection);
  const hasAlertsLoading = alertSections.some(isLoadingSection);
  const firstLoadingSummarySection = summarySections.find(({section}) =>
    isLoadingSection(section)
  )?.name;
  const namedOpenSections = [
    {name: 'openInterests' as const, section: viewModel.openInterests},
    {name: 'openBusinessCases' as const, section: viewModel.openBusinessCases},
    {name: 'openServiceCases' as const, section: viewModel.openServiceCases},
  ];
  const openSections = namedOpenSections.map(({section}) => section);
  // Each kind keeps its own list and its own count. Merging them into one list
  // hid whole kinds behind a shared limit and left them without a way in.
  const openGroups = namedOpenSections
    .map(({name, section}) => ({
      name,
      records: getNewestCustomerInsightsRecords(
        match(section)
          .returnType<CustomerInsightsRecord[]>()
          .with({status: 'ready'}, {status: 'degraded'}, ({data}) => data)
          .otherwise(() => [])
      ),
      total: match(section)
        .returnType<number | undefined>()
        .with({status: 'ready'}, {status: 'degraded'}, ({total}) =>
          match(total)
            .with(Pattern.number, (knownTotal) => knownTotal)
            .otherwise(() => undefined)
        )
        .otherwise(() => undefined),
    }))
    .filter(({records}) => records.length > 0);
  const showOpenItems = openGroups.length > 0;
  const hasOpenItemsLoading = openSections.some(isLoadingSection);
  const hasUnavailableOpenItemsCount = openSections.some((section) =>
    match(section)
      .with({status: 'loading'}, {status: 'error'}, () => true)
      .with({status: 'ready'}, {status: 'degraded'}, ({total}) =>
        match(total)
          .with(Pattern.number, () => false)
          .otherwise(() => true)
      )
      .otherwise(() => false)
  );
  const openSectionTotals = openSections.flatMap((section) =>
    match(section)
      .with({status: 'ready'}, {status: 'degraded'}, ({total}) =>
        match(total)
          .with(Pattern.number, (knownTotal) => [knownTotal])
          .otherwise(() => [])
      )
      .otherwise(() => [])
  );
  const openItemsCount = match(hasUnavailableOpenItemsCount)
    .with(true, () => undefined)
    .with(false, () => openSectionTotals.reduce((total, sectionTotal) => total + sectionTotal, 0))
    .exhaustive();
  const showCurrentActivity =
    showLastContact || showAlerts || hasAlertsLoading || showOpenItems || hasOpenItemsLoading;

  return {
    failedRetries,
    showFailure,
    showPartialFailure,
    showEmptyCustomer,
    showLastContact,
    showAlerts,
    hasAlertsLoading,
    firstLoadingSummarySection,
    openGroups,
    showOpenItems,
    hasOpenItemsLoading,
    openItemsCount,
    showCurrentActivity,
  };
}
